/* DemoPool · Black mirror ball + pixel portal
   - a black mirror ball with fine tiles; silver flashes as tiles turn into the light
   - hover melts the ball: tiles under the cursor fade to darkness, then turn into holes (a background can show through later);
     every movement melts a little more, and the tiles slowly grow back when the cursor leaves
   - every 8 bars the ball exhales into one swirling cloud of molecules and breathes back in
   - inside the ball the melted holes open a second photo (data-ball-src), fixed like a window behind the turning tiles
   - the cursor reveals a photo behind the hero in square pixels (<canvas data-dp-disco data-reveal-src="festival.jpg">);
     pixels switch on inside its radius and fade out after it moves on; the photo also shows through the melted tiles
   Canvas 2D, no libraries. Mount: <canvas data-dp-disco></canvas>.
   Pauses off-screen / hidden tab; still frame for prefers-reduced-motion. */
(() => {
  const cv = document.querySelector('[data-dp-disco]');
  if (!cv) return;
  // 10.10.2026 perf: start after the page has loaded and gone idle, so the ball never competes with LCP / first input;
  // the canvas fades in (.is-on). Phones and touch screens draw at 30 fps and skip tiles that are off the canvas.
  const boot = () => {
  const ctx = cv.getContext('2d');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const small = () => innerWidth < 750;
  const BEAT = 60 / 126, BAR = BEAT * 4, PHRASE = BAR * 8;
  const TAU = Math.PI * 2;

  /* ---- mirror ball tiles ---- */
  const TLAT = small() ? 36 : 54, TLON = small() ? 72 : 108;
  const tiles = [];
  for (let i = 0; i < TLAT; i++) {
    const p0 = -Math.PI / 2 + Math.PI * i / TLAT, p1 = -Math.PI / 2 + Math.PI * (i + 1) / TLAT;
    const n = Math.max(6, Math.round(TLON * Math.cos((p0 + p1) / 2)));
    const off = Math.random() * TAU;
    for (let j = 0; j < n; j++) {
      const pm = (p0 + p1) / 2, tm = off + TAU * (j + .5) / n;
      // model-space normal of the flat mirror, with the small random tilt every glued tile has (~1.5 deg)
      const nx = Math.cos(pm) * Math.cos(tm), ny = Math.sin(pm), nz = Math.cos(pm) * Math.sin(tm), J = .042;
      const jn = [nx + (Math.random() - .5) * J * 2, ny + (Math.random() - .5) * J * 2, nz + (Math.random() - .5) * J * 2];
      const jl = Math.hypot(...jn);
      tiles.push({ p0, p1, t0: off + TAU * j / n, t1: off + TAU * (j + 1) / n, s: Math.random(), s2: Math.random(), h: 0, jn: jn.map(v => v / jl) });
    }
  }

  /* ---- layout ---- */
  let W = 0, H = 0, dpr = 1, R = 100, CX = 0, CY = 0;
  let mx = -1e4, my = -1e4, tx = -1e4, ty = -1e4, lastMove = 0, pmx = -1e4, pmy = -1e4;
  let CS = 14, cols = 0, rows = 0, life = new Float32Array(0), seed = new Float32Array(0);
  const space = document.createElement('canvas'), sctx = space.getContext('2d');
  let revealImg = null;
  // ball photos: data-ball-src="ball,ball2,ball3" - they take turns; a switch happens while no hole is open
  // (invisible), or as a slow crossfade if someone keeps the holes open
  const balls = [];                         // { img, cv, pat }
  let cur = 0, nxt = -1, mix = 0, lastSwap = 0, anyHole = false;
  const SWAP_EVERY = 14, FADE = 1.8;
  // the second photo lives only inside the ball: cover-fit into the ball's square, clipped to its circle
  const paintOne = b => {
    b.pat = null;
    const ballImg = b.img;
    if (!ballImg || !ballImg.complete || !ballImg.naturalWidth) return;
    const ballSpace = b.cv, bctx = b.cv.getContext('2d');
    ballSpace.width = Math.round(W * dpr); ballSpace.height = Math.round(H * dpr);
    bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    bctx.save(); bctx.beginPath(); bctx.arc(CX, CY, R * 1.01, 0, TAU); bctx.clip();
    // lens: the photo is wrapped onto the sphere, so it bulges - magnified in the middle, compressed at the rim
    const ldpr = small() ? 1 : dpr;   // phones: lens at 1x = 4x fewer pixels (one long task less); the photo only shows inside melted tiles
    const Dp = Math.max(2, Math.round(R * 2.02 * ldpr)), Rp = Dp / 2;
    const src = document.createElement('canvas'); src.width = src.height = Dp;
    const sx = src.getContext('2d', { willReadFrequently: true });
    const ir = ballImg.naturalWidth / ballImg.naturalHeight;
    const w = ir > 1 ? Dp * ir : Dp, h = ir > 1 ? Dp : Dp / ir;
    sx.drawImage(ballImg, (Dp - w) / 2, (Dp - h) / 2, w, h);
    let sd; try { sd = sx.getImageData(0, 0, Dp, Dp).data; } catch (e) { sd = null; }   // cross-origin without CORS: fall back to the flat photo
    const out = sx.createImageData(Dp, Dp), od = out.data;
    const K = 2 / Math.PI;
    if (sd) for (let y = 0; y < Dp; y++) for (let x = 0; x < Dp; x++) {
      const dx = (x + .5 - Rp) / Rp, dy = (y + .5 - Rp) / Rp, r = Math.hypot(dx, dy);
      if (r >= 1) continue;
      const f = r > 1e-6 ? K * Math.asin(r) / r : K;            // sphere mapping: screen radius -> arc length on the ball
      const u = Math.min(Dp - 1, Math.max(0, Math.round(Rp + dx * f * Rp))), v = Math.min(Dp - 1, Math.max(0, Math.round(Rp + dy * f * Rp)));
      const si = (v * Dp + u) * 4, oi = (y * Dp + x) * 4;
      const nz = Math.sqrt(1 - r * r);                           // light: soft falloff to the rim + a faint glassy highlight top-left
      const hl = Math.max(0, 1 - Math.hypot(dx + .38, dy + .42) / .55);
      const shade = .42 + .58 * Math.pow(nz, .6), add = 70 * hl * hl;
      od[oi] = Math.min(255, sd[si] * shade + add); od[oi + 1] = Math.min(255, sd[si + 1] * shade + add); od[oi + 2] = Math.min(255, sd[si + 2] * shade + add); od[oi + 3] = 255;
    }
    if (sd) sx.putImageData(out, 0, 0);
    bctx.setTransform(1, 0, 0, 1, 0, 0);
    const Dd = Math.round(R * 2.02 * dpr);
    bctx.drawImage(src, Math.round((CX - R * 1.01) * dpr), Math.round((CY - R * 1.01) * dpr), Dd, Dd);
    bctx.restore();
    b.pat = ctx.createPattern(ballSpace, 'no-repeat');
    if (b.pat && b.pat.setTransform) b.pat.setTransform(new DOMMatrix([1 / dpr, 0, 0, 1 / dpr, 0, 0]));
  };
  const paintBall = () => balls.forEach(paintOne);
  const ready = i => balls[i] && balls[i].pat;
  const swapTick = t => {                   // decide when the next photo takes over
    if (balls.length < 2) return;
    if (nxt < 0 && t - lastSwap > SWAP_EVERY) {
      for (let k = 1; k < balls.length; k++) { const j = (cur + k) % balls.length; if (ready(j)) { nxt = j; break; } }
      if (nxt >= 0 && !anyHole) { cur = nxt; nxt = -1; lastSwap = t; }   // nobody sees the inside: switch instantly
    }
    if (nxt >= 0) { mix = Math.min(1, mix + dtLast / FADE); if (mix >= 1) { cur = nxt; nxt = -1; mix = 0; lastSwap = t; } }
  };

  const RN = 120; let rimCol = [], rimA = new Float32Array(RN);
  const sampleRim = () => {
    rimCol = [];
    try {
      const d = sctx.getImageData(0, 0, space.width, space.height).data;
      for (let k = 0; k < RN; k++) {
        const a = k / RN * TAU; let r = 0, g = 0, b = 0, n = 0;
        for (let o = 4; o <= 28; o += 6) {
          const x = Math.round((CX + Math.cos(a) * (R + o)) * dpr), y = Math.round((CY + Math.sin(a) * (R + o)) * dpr);
          if (x < 0 || y < 0 || x >= space.width || y >= space.height) continue;
          const i = (y * space.width + x) * 4; r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
        }
        n ? (r /= n, g /= n, b /= n) : (r = g = b = 120);
        const m = Math.max(r, g, b, 1), boost = Math.min(2.2, 235 / m);   // keep the hue, lift it to a light
        rimCol.push([r * boost | 0, g * boost | 0, b * boost | 0]);
      }
    } catch (e) { for (let k = 0; k < RN; k++) rimCol.push([220, 200, 210]); }
  };
  const paintSpace = () => {
    space.width = Math.round(W * dpr); space.height = Math.round(H * dpr);
    sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (revealImg && revealImg.complete && revealImg.naturalWidth) {       // image mode: cover-fit the picture
      const ir = revealImg.naturalWidth / revealImg.naturalHeight, cr = W / H;
      const w = ir > cr ? H * ir : W, h = ir > cr ? H : W / ir;
      sctx.drawImage(revealImg, (W - w) / 2, (H - h) / 2, w, h);
      sampleRim();
      return;
    }
    // starfield: deep space, a faint nebula and three layers of stars
    sctx.fillStyle = '#04040a'; sctx.fillRect(0, 0, W, H);
    const neb = (x, y, r, c) => { const g = sctx.createRadialGradient(x, y, 0, x, y, r); g.addColorStop(0, c); g.addColorStop(1, 'rgba(0,0,0,0)'); sctx.fillStyle = g; sctx.fillRect(0, 0, W, H); };
    neb(W * .3, H * .35, W * .35, 'rgba(90,70,160,.22)'); neb(W * .7, H * .7, W * .3, 'rgba(160,80,120,.14)'); neb(W * .55, H * .2, W * .25, 'rgba(60,110,170,.12)');
    const count = Math.round(W * H / 900);
    for (let i = 0; i < count; i++) {
      const x = Math.random() * W, y = Math.random() * H, m = Math.random();
      const r = m > .985 ? 1.6 + Math.random() * 1.2 : m > .9 ? .9 + Math.random() * .6 : .35 + Math.random() * .5;
      const tint = Math.random(); const c = tint > .92 ? '255,214,170' : tint > .84 ? '190,205,255' : '255,255,255';
      sctx.fillStyle = `rgba(${c},${(.35 + Math.random() * .65).toFixed(2)})`;
      sctx.beginPath(); sctx.arc(x, y, r, 0, TAU); sctx.fill();
      if (r > 1.5) { const g = sctx.createRadialGradient(x, y, 0, x, y, r * 6); g.addColorStop(0, `rgba(${c},.35)`); g.addColorStop(1, 'rgba(0,0,0,0)'); sctx.fillStyle = g; sctx.fillRect(x - r * 6, y - r * 6, r * 12, r * 12); }
    }
  };
  const size = () => {
    dpr = Math.min(devicePixelRatio || 1, 2);
    const b = cv.getBoundingClientRect(); W = b.width; H = b.height;
    cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr); ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (small()) { R = W * .62; CX = W * 1.04; CY = H * .5; } else { /* phone: big ball, >half off the right edge */ R = Math.min(W * .2, H * .42); CX = W * .73; CY = H * .5; }
    CS = small() ? 12 : 16; cols = Math.ceil(W / CS); rows = Math.ceil(H / CS);
    life = new Float32Array(cols * rows); seed = Float32Array.from({ length: cols * rows }, () => Math.random());
    paintSpace(); paintBall();
  };
  // photos: AVIF first, WebP fallback, small files on phones; fetched after the page has painted (no LCP cost)
  // data-reveal-src="bg" data-ball-src="ball" + data-img-base="img/" -> img/bg.avif | img/bg.webp | img/bg-sm.avif ...
  const loadPic = (name, done) => {
    if (!name) return null;
    const base = cv.dataset.imgBase || '', sm = innerWidth * Math.min(devicePixelRatio || 1, 2) < 1300 ? '-sm' : '';
    const direct = /[./:]/.test(name);                                  // a full URL (e.g. Shopify image_url) is used as is
    const im = new Image(); im.decoding = 'async'; im.crossOrigin = 'anonymous'; im.onload = done;   // CORS so the lens can read pixels (Shopify CDN allows it)
    if (!direct) im.onerror = () => { im.onerror = null; im.src = base + name + sm + '.webp'; };
    im.src = direct ? name : base + name + sm + '.avif';
    return im;
  };
  const later = f => ('requestIdleCallback' in window ? requestIdleCallback(f, { timeout: 1200 }) : setTimeout(f, 300));
  // the photos are only seen when someone points at the hero (pixel portal, melted tiles): fetch them on the first pointer, not on load
  let picsAsked = false;
  const loadPics = () => { if (picsAsked) return; picsAsked = true; later(() => {
    revealImg = loadPic(cv.dataset.revealSrc, () => { paintSpace(); paintBall(); });
    (cv.dataset.ballSrc || '').split(',').map(x => x.trim()).filter(Boolean).forEach((name, i) => {
      const b = { cv: document.createElement('canvas'), pat: null, img: null };
      balls.push(b);
      // first photo right away, the others a little later so they never compete with the page
      const go = () => { b.img = loadPic(name, () => paintOne(b)); };
      i ? setTimeout(go, 2500 + i * 800) : go();
    });
  }); };
  size();
  // keep the canvas buffer in sync with its box (fonts and layout can change the hero height after load)
  const resized = () => { size(); if (!running) draw(2); };   // still frame survives a resize before the loop starts
  if ('ResizeObserver' in window) new ResizeObserver(resized).observe(cv); else addEventListener('resize', resized, { passive: true });
  const host = cv.closest('section') || cv.parentElement;
  const onPtr = e => { const b = cv.getBoundingClientRect(); tx = e.clientX - b.left; ty = e.clientY - b.top; if (mx < -9e3) { mx = tx; my = ty; } lastMove = performance.now(); };
  host.addEventListener('pointerdown', onPtr, { passive: true });
  host.addEventListener('pointermove', e => { const b = cv.getBoundingClientRect(); tx = e.clientX - b.left; ty = e.clientY - b.top; if (mx < -9e3) { mx = tx; my = ty; } lastMove = performance.now(); }, { passive: true });
  host.addEventListener('pointerleave', () => { tx = ty = -1e4; }, { passive: true });
  host.addEventListener('pointerdown', loadPics, { passive: true, once: true });
  host.addEventListener('pointermove', loadPics, { passive: true, once: true });

  const sm = (a, b, x) => { const u = Math.min(1, Math.max(0, (x - a) / (b - a))); return u * u * (3 - 2 * u); };
  const unit = v => { const l = Math.hypot(...v); return v.map(x => x / l); };
  // Lighting model of a real mirror ball: every tile is a flat mirror, so it lights up only when its normal points
  // along the half-vector between the light and the eye. One key spotlight (upper left, in front) makes the main
  // hotspot; a weaker rim light behind-right catches the edge. The tile's tiny random tilt scatters the hotspot into
  // a cluster of fully lit tiles that flash on and off as the ball turns. The rest reflects a dark room.
  const VIEW = [0, 0, 1];
  const KEY = unit([-.55, -.62, .56]), RIM = unit([.85, -.25, -.35]);
  const HK = unit([KEY[0] + VIEW[0], KEY[1] + VIEW[1], KEY[2] + VIEW[2]]), HR2 = unit([RIM[0] + VIEW[0], RIM[1] + VIEW[1], RIM[2] + VIEW[2]]);
  const spot = (d, inner, outer) => d >= inner ? 1 : d <= outer ? 0 : ((d - outer) / (inner - outer)) ** 2;

  let prevT = 0, running = false;
  let dtLast = 1 / 60;
  const draw = (t) => {
    const dt = Math.min(.05, t - prevT); prevT = t; dtLast = dt; swapTick(t);
    ctx.clearRect(0, 0, W, H);
    if (tx < -9e3) { mx = my = -1e4; } else { mx += (tx - mx) * .2; my += (ty - my) * .2; }

    /* ---- pixel portal ---- */
    const PR = small() ? 120 : 190, BR2 = Math.max(0, R - CS * .75) ** 2;
    const active = mx > -9e3 && !!(revealImg && revealImg.naturalWidth);
    if (active) {
      const c0 = Math.max(0, Math.floor((mx - PR) / CS)), c1 = Math.min(cols - 1, Math.ceil((mx + PR) / CS));
      const r0 = Math.max(0, Math.floor((my - PR) / CS)), r1 = Math.min(rows - 1, Math.ceil((my + PR) / CS));
      for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) {
        const px = (c + .5) * CS, py = (r + .5) * CS;
        if ((px - CX) ** 2 + (py - CY) ** 2 < BR2) continue;               // the ball has its own photo
        const i = r * cols + c, d = Math.hypot(px - mx, py - my) / PR;
        if (d < 1 && d < .6 + .4 * seed[i]) life[i] = Math.min(1, life[i] + dt * (12 - 6 * d));   // centre first, ragged pixel edge
      }
    }
    ctx.save();                                                          // pixels stop exactly at the ball's edge
    ctx.beginPath(); ctx.rect(0, 0, W, H); ctx.arc(CX, CY, R, 0, TAU, true); ctx.clip('evenodd');
    for (let i = 0; i < life.length; i++) {
      const l = life[i]; if (l <= 0) continue;
      life[i] = Math.max(0, l - dt * (.32 + seed[i] * .3));   // lingers ~2-3 s
      const c = i % cols, r = (i / cols) | 0, x = c * CS, y = r * CS;
      if ((x + CS / 2 - CX) ** 2 + (y + CS / 2 - CY) ** 2 < BR2) { life[i] = 0; continue; }
      const e = sm(0, 1, l), s = CS * (.55 + .45 * e) - 1;               // pixels pop in / shrink out
      ctx.globalAlpha = e;
      ctx.drawImage(space, (x + (CS - s) / 2) * dpr, (y + (CS - s) / 2) * dpr, s * dpr, s * dpr, x + (CS - s) / 2, y + (CS - s) / 2, s, s);
    }
    ctx.globalAlpha = 1; ctx.restore();

    /* ---- black mirror ball ---- */
    const beat = t / BEAT, bph = beat % 1, ph = t % PHRASE;
    const spin = t * .045, cS = Math.cos(spin), sS = Math.sin(spin);
    const bx = .1, cBx = Math.cos(bx), sBx = Math.sin(bx);   // hangs straight; camera a touch below the ball
    const ball = (x, y, z) => { [x, z] = [x * cS - z * sS, x * sS + z * cS]; return [x, y * cBx - z * sBx, y * sBx + z * cBx]; };
    const scr = v => { const f = 6 / (6 - v[2]); return [CX + v[0] * R * f, CY + v[1] * R * f, v[2]]; };
    // one-body exhale / inhale every 8 bars
    const u = 0;   // dissolve switched off for now
    const solid = 1 - sm(.02, .22, u), dustA = sm(0, .1, u);

    // cursor speed: every movement melts a little more
    const cspd = mx > -9e3 && pmx > -9e3 ? Math.min(1.5, Math.hypot(mx - pmx, my - pmy) / Math.max(dt, .001) / 900) : 0;
    pmx = mx; pmy = my;
    const MR = R * .5;
    let holes = 0;
    for (const tl of tiles) {
      const pm = (tl.p0 + tl.p1) / 2, tm = (tl.t0 + tl.t1) / 2;
      const n = ball(Math.cos(pm) * Math.cos(tm), Math.sin(pm), Math.cos(pm) * Math.sin(tm));
      if (n[2] < .03) continue;
      const c = scr(n);
      if (c[0] < -40 || c[0] > W + 40 || c[1] < -40 || c[1] > H + 40) continue;   // off the canvas (phones: most of the ball is past the right edge)
      const m = ball(tl.jn[0], tl.jn[1], tl.jn[2]);                 // the tile's real (slightly tilted) mirror normal
      const dK = m[0] * HK[0] + m[1] * HK[1] + m[2] * HK[2];
      const dR = m[0] * HR2[0] + m[1] * HR2[1] + m[2] * HR2[2];
      const key = spot(dK, .9982, .991) + .45 * spot(dK, .985, .972) * (tl.s2 > .55 ? 1 : .25) + .12 * Math.pow(Math.max(0, dK), 160);   // hot core + scattered halo  // spotlight: hard-edged, whole tile
      const rim = .55 * spot(dR, .998, .99);
      const ry = 2 * m[2] * m[1];                                               // reflected view ray, y part (up < 0)
      const room = .018 + .035 * Math.max(0, -ry) + .07 * Math.pow(1 - m[2], 3);   // dim ceiling + fresnel at the edge
      const spec = key + rim;
      // melt under the cursor (hole grows with movement, heals slowly)
      const dm = Math.hypot(c[0] - mx, c[1] - my);
      if (dm < MR) tl.h = Math.min(1, tl.h + dt * (1 - dm / MR) * (1.3 + 4.5 * cspd) * (.7 + .6 * tl.s));
      else if (tl.h > 0) tl.h = Math.max(0, tl.h - dt * (.09 + .06 * tl.s));   // holes stay open ~6-8 s
      const h = tl.h, dark = Math.min(1, h * 2), gone = Math.max(0, h * 2 - 1);   // first darkness, then a hole
      const q = [[tl.p0, tl.t0], [tl.p0, tl.t1], [tl.p1, tl.t1], [tl.p1, tl.t0]].map(([p, th]) => scr(ball(Math.cos(p) * Math.cos(th), Math.sin(p), Math.cos(p) * Math.sin(th))));
      if (gone > 0) holes++;
      if (gone > 0 && ready(cur)) {   // the hole shows its own piece of the ball photo (crossfading while a swap runs)
        const ga = Math.min(1, gone * 1.6);
        ctx.beginPath(); q.forEach((p, i) => i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])); ctx.closePath();
        ctx.globalAlpha = ga; ctx.fillStyle = balls[cur].pat; ctx.fill();
        if (nxt >= 0 && mix > 0) { ctx.globalAlpha = ga * mix; ctx.fillStyle = balls[nxt].pat; ctx.fill(); }
        ctx.globalAlpha = 1;
      }
      if (gone >= .999) continue;
      const lum = Math.min(1, room + .035 * tl.s2 * tl.s2 + spec) * (1 - dark);
      if (solid > .01) {
        const a = solid * (1 - gone);
        // body of the cell (the grout between mirrors)
        ctx.beginPath(); q.forEach((p, i) => i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])); ctx.closePath();
        ctx.fillStyle = `rgba(4,4,5,${a.toFixed(3)})`; ctx.fill();
        // the mirror; it shrinks a little as it melts away
        const k = .88 * (1 - .4 * gone);
        ctx.beginPath();
        q.forEach((p, i) => { const x = c[0] + (p[0] - c[0]) * k, y = c[1] + (p[1] - c[1]) * k; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
        ctx.closePath();
        const v = 6 + 249 * lum, warm = Math.min(1, key) * (1 - dark), cool = Math.min(1, rim) * (1 - dark);   // key light slightly warm, rim slightly cool
        ctx.fillStyle = `rgba(${Math.round(v + 4 * warm - 6 * cool)},${Math.round(v)},${Math.round(Math.min(255, v + 3 - 6 * warm + 8 * cool))},${a.toFixed(3)})`; ctx.fill();
      }
      if (u > 0) {   // molecules: the ball exhales into one swirling cloud
        const rx = c[0] - CX, ry = c[1] - CY, sw = u * 1.1 + u * u * .4, cs = Math.cos(sw), sn = Math.sin(sw), ex = 1 + u * .5;
        const j = u * (10 + 22 * tl.s);
        const x = CX + (rx * cs - ry * sn) * ex + 40 * u + Math.sin(tl.s * 40 + t * .4) * j;
        const y = CY + (rx * sn + ry * cs) * ex * (1 - .25 * u) - 30 * u + Math.cos(tl.s * 30 + t * .35) * j;
        const a = dustA * (.6 - .2 * u) * (.4 + .6 * Math.max(lum * 3, tl.s));
        const s = 1.2 + 1.3 * tl.s;
        ctx.fillStyle = `rgba(235,236,242,${a.toFixed(3)})`; ctx.fillRect(x - s / 2, y - s / 2, s, s);
      }
    }
    anyHole = holes > 0;
    // rim light: where the photo is open next to the ball, its light wraps around the edge
    if (rimCol.length) {
      ctx.globalCompositeOperation = 'lighter';
      for (let k = 0; k < RN; k++) {
        const a = (k + .5) / RN * TAU; let m = 0;
        for (let o = CS * .5; o <= CS * 2.6; o += CS) {
          const c = Math.floor((CX + Math.cos(a) * (R + o)) / CS), r = Math.floor((CY + Math.sin(a) * (R + o)) / CS);
          if (c >= 0 && r >= 0 && c < cols && r < rows) m = Math.max(m, life[r * cols + c] * (1 - (o - CS * .5) / (CS * 2.6)));
        }
        rimA[k] += (m - rimA[k]) * Math.min(1, dt * 6);
      }
      for (let k = 0; k < RN; k++) {
        const v = (rimA[(k + RN - 1) % RN] + 2 * rimA[k] + rimA[(k + 1) % RN]) / 4;
        if (v < .01) continue;
        const a0 = k / RN * TAU - .004, a1 = (k + 1) / RN * TAU + .004, col = rimCol[k];
        ctx.strokeStyle = `rgba(${col[0]},${col[1]},${col[2]},${(v * .22).toFixed(3)})`; ctx.lineWidth = 7;
        ctx.beginPath(); ctx.arc(CX, CY, R - 3.5, a0, a1); ctx.stroke();
        ctx.strokeStyle = `rgba(${col[0]},${col[1]},${col[2]},${(v * .75).toFixed(3)})`; ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.arc(CX, CY, R - .8, a0, a1); ctx.stroke();
      }
    }
    ctx.globalCompositeOperation = 'source-over';
    // hairline rim so the black ball reads against the black page
    if (solid > .01) { ctx.strokeStyle = `rgba(255,255,255,${(.07 * solid).toFixed(3)})`; ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(CX, CY, R, Math.PI * .95, Math.PI * 2.05); ctx.stroke(); }
  };

  let visible = true;
  new IntersectionObserver(e => { visible = e[0].isIntersecting; }, { rootMargin: '80px' }).observe(cv);
  const t0 = performance.now();
  cv.classList.add('is-on');
  // 16.10.2026 perf (seo-fix-pack): paint ONE still frame after load, start the animation loop on the first interaction
  // (pointer, touch, scroll, key). Lab mobile: the loop was ~9 s of main-thread work in the first 20 s of a visit (TBT 5-6 s on the
  // home page) for a visitor who had not touched the page yet. Desktop starts on the first mouse move, so it looks the same.
  draw(2);
  if (reduce) return;
  const lowFps = small() || matchMedia('(pointer: coarse)').matches;   // the ball turns slowly: 30 fps looks the same, costs half
  let lastFrame = 0, tStart = 0;
  // 01.11.2026 INP (Clarity 430 ms): skip frames for 300 ms after every tap / key / click, so the input's next paint never waits on the ball
  let yieldUntil = 0;
  ['pointerdown', 'keydown', 'click'].forEach(e => addEventListener(e, () => { yieldUntil = performance.now() + 300; }, { capture: true, passive: true }));
  const loop = now => {
    requestAnimationFrame(loop);
    if (!visible || document.hidden || now < yieldUntil) return;
    if (lowFps && now - lastFrame < 31) return;
    lastFrame = now; draw(2 + (now - tStart) / 1000);   // continue from the still frame (t = 2 s), no jump
  };
  const ev = ['pointermove', 'pointerdown', 'touchstart', 'scroll', 'wheel', 'keydown'];
  // the first frame waits until the interaction that started it has painted (was: same task -> long INP on the first click)
  const start = () => { ev.forEach(e => removeEventListener(e, start, true)); running = true; setTimeout(() => { tStart = performance.now(); requestAnimationFrame(loop); }, 350); };
  ev.forEach(e => addEventListener(e, start, { capture: true, passive: true }));
  };
  // 01.11.2026 INP: phones and touch screens get no canvas at all (no image decode, no tile build, no frames); the hero keeps its static backdrop
  if (innerWidth < 750 || matchMedia('(pointer: coarse)').matches) return;
  const go = () => ('requestIdleCallback' in window ? requestIdleCallback(boot, { timeout: 1500 }) : setTimeout(boot, 200));
  if (document.readyState === 'complete') go(); else addEventListener('load', go, { once: true });
})();
